import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList } from "recharts";
import { GRID } from "@/lib/charts";
import { COMP_COLORS, REC, CONF, fmtFit } from "@/lib/fit";
import { cn } from "@/lib/utils";

export const RecChip = ({ rec, testId }) => rec ? (
  <span data-testid={testId} className={cn("inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium", REC[rec][1])}>{REC[rec][0]}</span>
) : <span className="text-xs text-ink-muted">—</span>;

export const ConfLabel = ({ c }) => c ? <span className="text-xs text-ink-muted">{CONF[c]}</span> : null;

export function MiniBar({ components, testId }) {
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[#EFEBE3]" data-testid={testId} title={components.filter((c) => c.status === "complete").map((c) => `${c.label}: +${c.contribution}`).join(" · ")}>
      {components.filter((c) => c.status === "complete").map((c) => <span key={c.key} style={{ width: `${c.contribution * 10}%`, background: COMP_COLORS[c.key] }} />)}
    </div>
  );
}

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return <div className="rounded-lg border border-line bg-white px-3 py-2 text-xs shadow-sm"><p className="font-medium">{d.name}</p><p className="text-ink-secondary">{d.note || (d.total ? `Fit Score ${fmtFit(d.delta)}` : `${d.value}/100 × ${Math.round(d.w * 100)}% = +${d.delta}`)}</p></div>;
};

export function Waterfall({ fit }) {
  const used = fit.components.filter((c) => c.status !== "not_used");
  let run = 0;
  const data = used.map((c) => { const row = { name: c.label, key: c.key, base: run, delta: c.contribution, label: c.contribution ? `+${c.contribution.toFixed(2)}` : "", value: c.value, w: c.weight_used, note: c.status === "complete" ? "" : c.note }; run += c.contribution; return row; });
  data.push({ name: "Fit Score", key: "total", base: 0, delta: fit.fit_computed ?? 0, label: fmtFit(fit.fit_computed), total: true });
  return (
    <div data-testid="fit-waterfall">
      <ResponsiveContainer width="100%" initialDimension={{ width: 420, height: 220 }} height={230}>
        <BarChart data={data} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#575B54" }} interval={0} />
          <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11, fill: "#8A8D85" }} />
          <Tooltip content={<Tip />} cursor={{ fill: "#FAF8F4" }} />
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="delta" stackId="w" radius={[5, 5, 0, 0]} isAnimationActive={false}>
            <LabelList dataKey="label" position="top" fontSize={10} fill="#575B54" />
            {data.map((d) => <Cell key={d.key} fill={d.total ? "#9A6700" : COMP_COLORS[d.key]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
        {used.filter((c) => c.status !== "complete").map((c) => <span key={c.key} data-testid={`waterfall-note-${c.key}`}>{c.label}: {c.note}</span>)}
        {used.filter((c) => c.overridden).map((c) => <span key={`o-${c.key}`} className="text-[#7A5300]">{c.label}: overridden (was {c.original ?? "—"})</span>)}
      </div>
    </div>
  );
}
